import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { vortxColors } from '@vortx/shared-ui';

export default function LoadProfileChart({ loads = [], color = vortxColors.teal, areaColor = vortxColors.teal }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const data = useMemo(() => {
        if (!loads || loads.length === 0) return [];
        // Assuming 8760 format starting Jan 1st 00:00
        const start = new Date('2024-01-01T00:00:00'); // Year doesn't matter much for display
        return loads.map((val, idx) => {
            const time = new Date(start.getTime() + idx * 3600 * 1000);
            return {
                timestamp: time.getTime(),
                load: val
            };
        });
    }, [loads]);

    if (!loads || loads.length === 0) return null;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
                <defs>
                    <linearGradient id="colorLoad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={areaColor} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={areaColor} stopOpacity={0.1} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.1)' : '#eee'} />
                <XAxis
                    dataKey="timestamp"
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={(unix) => new Date(unix).toLocaleString('default', { month: 'short' })}
                    stroke={theme.palette.text.secondary}
                    minTickGap={30}
                    style={{ fontSize: 12 }}
                />
                <YAxis
                    stroke={theme.palette.text.secondary}
                    tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                    style={{ fontSize: 12 }}
                />
                <Tooltip
                    labelFormatter={(unix) => new Date(unix).toLocaleString()}
                    formatter={(val) => [`${Math.round(val)} kW`, 'Load']}
                    contentStyle={{
                        borderRadius: 8,
                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary
                    }}
                    labelStyle={{ color: theme.palette.text.secondary }}
                />
                <Area
                    type="monotone"
                    dataKey="load"
                    stroke={color}
                    fillOpacity={1}
                    fill="url(#colorLoad)"
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}
